import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import {JWT_ACCESS_TOKEN, JWT_REFRESH_TOKEN} from '../constants/constants';
import {baseUrl} from '../constants/base-url';
import {BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError} from 'rxjs';
import {AuthService} from '../services/auth.service';
import {JwtTokens} from '../types/auth.types';
import {inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {AuthApiService} from "../services-api/auth-api.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private authService = inject(AuthService);
  private authApiService = inject(AuthApiService);
  private router = inject(Router);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const jwtAccessToken: string | null = this.getJwtAccessToken();
    const jwtRefreshToken: string | null = this.getJwtRefreshToken();

    if (!jwtAccessToken) { return next.handle(request);  }

    if (request.url === `${baseUrl}/refresh`) {
      if (!jwtRefreshToken) { return next.handle(request); }

      return next.handle(this.addTokenToHeader(request, jwtRefreshToken));
    }

    return next.handle(this.addTokenToHeader(request, jwtAccessToken))
      .pipe(catchError(error => {
        if (error.status === 401) {
          return this.handle401Error(request, next);
        }

        return throwError(error);
      }
    ))
  }

  private addTokenToHeader(request: HttpRequest<any>, token: string) {
    return request.clone({setHeaders: { Authorization: `Bearer ${token}`} });
  }

  private getJwtAccessToken(): string | null {
    return localStorage.getItem(JWT_ACCESS_TOKEN);
  }

  private getJwtRefreshToken(): string | null {
    return localStorage.getItem(JWT_REFRESH_TOKEN);
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap((token) => next.handle(this.addTokenToHeader(request, token)))
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.authApiService.refreshTokens().pipe(
      switchMap((jwtTokens: JwtTokens) => {
        this.isRefreshing = false;
        this.authService.storeJwtTokens(jwtTokens);
        this.refreshTokenSubject.next(jwtTokens.access);

        return next.handle(this.addTokenToHeader(request, jwtTokens.access));
      }),
      catchError((err) => {
        this.isRefreshing = false;
        this.authService.logout();
        this.router.navigate(['/login']).then();

        return throwError(err);
      })
    )
  }
}
